import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import { useParams } from 'react-router-dom'

import ImageReport from '../ImageReport/ImageReport'
import VideoReport from '../VideoReport/VideoReport'

export type MediaImagePagesProps = c.r<{
  isPDF: boolean

  houseViewPictures: c.MediaData[]

  // Assessment
  atticPictures: c.MediaData[]
  roofPictures: c.MediaData[]
  windowPictures: c.MediaData[]
  hvacPictures: c.MediaData[]
  otherAreasPictures: c.MediaData[]

  // Documentation
  idPictures: c.MediaData[]
  otherDocumentsPictures: c.MediaData[]

  // Solar survey
  thermalPictures: c.MediaData[]
  solarProposalPictures: c.MediaData[]
  electricityPictures: c.MediaData[]
  allExteriorWallsPics: c.MediaData[]
  raftersPics: c.MediaData[]
  allPlanesRoofPics: c.MediaData[]
  electricalPictures: c.MediaData[]
  otherSolarSurveyPics: c.MediaData[]
  adjustmentPictures: c.MediaData[]
  electricalOldPictures: c.MediaData[]

  // Roof claims
  rcFrontHousePictures: c.MediaData[]
  rcAddressVerificationPictures: c.MediaData[]
  rc3DViewPictures: c.MediaData[]
  rcGaragePics: c.MediaData[]
  rcWiresHomePictures: c.MediaData[]
  rcSlidingPictures: c.MediaData[]
  rcLayersPictures: c.MediaData[]
  rcInclinationPictures: c.MediaData[]
  rcRoofOverviewPictures: c.MediaData[]
  rcDripEdgePictures: c.MediaData[]
  rcGuttersPictures: c.MediaData[]
  rcIceShieldPics: c.MediaData[]
  rcFlashingsPictures: c.MediaData[]
  rcPenetrationsPictures: c.MediaData[]
  rcHailPictures: c.MediaData[]
  rcPropertyDamagesPics: c.MediaData[]
  rcWindDamagesPics: c.MediaData[]
  rcShingleLengthPics: c.MediaData[]
  rcShinglesColorPics: c.MediaData[]
}>

export default React.memo<MediaImagePagesProps>(function MediaImagePages({
  isPDF,

  houseViewPictures,

  // Assessment
  atticPictures,
  roofPictures,
  windowPictures,
  hvacPictures,
  otherAreasPictures,

  // Documentation
  idPictures,
  otherDocumentsPictures,

  // Solar Survey
  thermalPictures,
  solarProposalPictures,
  electricityPictures,
  allExteriorWallsPics,
  raftersPics,
  allPlanesRoofPics,
  electricalPictures,
  otherSolarSurveyPics,
  adjustmentPictures,
  electricalOldPictures,

  // Roof claims
  rcFrontHousePictures,
  rcAddressVerificationPictures,
  rc3DViewPictures,
  rcGaragePics,
  rcWiresHomePictures,
  rcSlidingPictures,
  rcLayersPictures,
  rcInclinationPictures,
  rcRoofOverviewPictures,
  rcDripEdgePictures,
  rcGuttersPictures,
  rcIceShieldPics,
  rcFlashingsPictures,
  rcPenetrationsPictures,
  rcHailPictures,
  rcPropertyDamagesPics,
  rcWindDamagesPics,
  rcShingleLengthPics,
  rcShinglesColorPics,
}) {
  const { customerID } = useParams<'customerID'>()
  const { currProducts } = r.useProducts(customerID!)
  const [company] = r.useCustomerField(customerID!, 'solarCompany')

  return (
    <>
      {houseViewPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.type === 'img' ? (
            <ImageReport
              company={company}
              image={image.url}
              isPDF={isPDF}
              title="House View From Street"
            />
          ) : (
            <VideoReport
              company={company}
              image={image.url}
              isPDF={isPDF}
              title="House View From Street"
            />
          )}
        </React.Fragment>
      ))}

      {/* Assessment */}
      {atticPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.type === 'img' ? (
            <ImageReport
              title="Attic Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Attic Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}
      {roofPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.url ? (
            <ImageReport
              title="Roof Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Roof Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}
      {windowPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.url ? (
            <ImageReport
              title="Windows Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Windows Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}
      {hvacPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.url ? (
            <ImageReport
              title="HVAC Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="HVAC Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}
      {otherAreasPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.type === 'img' ? (
            <ImageReport
              title="Other Areas"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Other Areas"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}

      {/* Documentation */}
      {idPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.type === 'img' ? (
            <ImageReport
              title="Id Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Id Pictures"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}
      {otherDocumentsPictures.map((image) => (
        <React.Fragment key={image.url}>
          {image.type === 'img' ? (
            <ImageReport
              title="Other Documents"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          ) : (
            <VideoReport
              title="Other Documents"
              image={image.url}
              isPDF={isPDF}
              company={company}
            />
          )}
        </React.Fragment>
      ))}

      {/* Solar Survey */}
      {(currProducts.includes('Solar') || currProducts.length === 0) && (
        <>
          {thermalPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Thermal Pictures"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Thermal Pictures"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {solarProposalPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Solar Proposal"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Solar Proposal"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {electricityPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Electricity Bill"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Electricity Bill"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {allExteriorWallsPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="All Exterior Walls"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="All Exterior Walls"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {raftersPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Rafters"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Rafters"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {allPlanesRoofPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="All planes roof"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="All planes roof"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {electricalPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Electrical Panel"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Electrical Panel"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {otherSolarSurveyPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Other (Site Survey)"
                />
              ) : (
                <VideoReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Other (Site Survey)"
                />
              )}
            </React.Fragment>
          ))}
          {adjustmentPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Adjustment"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Adjustment"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {electricalOldPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Electrical Panel"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Electrical Panel"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
        </>
      )}

      {(currProducts.includes('RC') || currProducts.length === 0) && (
        <>
          {rcFrontHousePictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Front of the house"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Front of the house"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcAddressVerificationPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Address Verification"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Address Verification"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rc3DViewPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="3D View"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="3D View"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcGaragePics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Garage"
                />
              ) : (
                <VideoReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Garage"
                />
              )}
            </React.Fragment>
          ))}
          {rcWiresHomePictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Wires in to home"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Wires in to home"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcSlidingPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Siding"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Siding"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcLayersPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Layers"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Layers"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcInclinationPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Roof Inclination angle"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Roof inclination angle"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcRoofOverviewPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Roof Overview"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Roof Overview"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcDripEdgePictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Drip Edge"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Drip Edge"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcGuttersPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Gutters"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Gutters"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcIceShieldPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Ice Shield"
                />
              ) : (
                <VideoReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Ice Shield"
                />
              )}
            </React.Fragment>
          ))}
          {rcFlashingsPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Flashings"
                />
              ) : (
                <VideoReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Flashings"
                />
              )}
            </React.Fragment>
          ))}
          {rcPenetrationsPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Penetrations"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Penetrations"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcHailPictures.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Hail"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Hail"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcPropertyDamagesPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Other property damages"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Other property damages"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcWindDamagesPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Wind damages"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Wind damages"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
          {rcShingleLengthPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Shingle length"
                />
              ) : (
                <VideoReport
                  company={company}
                  image={image.url}
                  isPDF={isPDF}
                  title="Shingle length"
                />
              )}
            </React.Fragment>
          ))}
          {rcShinglesColorPics.map((image) => (
            <React.Fragment key={image.url}>
              {image.type === 'img' ? (
                <ImageReport
                  title="Shingles Color"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              ) : (
                <VideoReport
                  title="Shingles Color"
                  image={image.url}
                  isPDF={isPDF}
                  company={company}
                />
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  )
})
